import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseConfigService } from "@fuse/services/config.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { locale as catalan } from "../i18n/ca";
import { locale as english } from "../i18n/en";
import { locale as spanish } from "../i18n/es";
import { AuthService } from "app/services/api/auth.service";
import { AuthenticationService } from "app/services/authentication.service";

@Component({
    selector: "reset-password",
    templateUrl: "./reset-password.component.html",
    styleUrls: ["./reset-password.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: false,
})
export class ResetPasswordComponent implements OnInit {
    resetPasswordForm: UntypedFormGroup;
    loading = false;
    error: string = null;
    success = false;
    minLength = 6;
    token: string = null;

    constructor(
        private fuseConfigService: FuseConfigService,
        private formBuilder: UntypedFormBuilder,
        private authService: AuthService,
        private authenticationService: AuthenticationService,
        private route: ActivatedRoute,
        private router: Router,
        private translation: FuseTranslationLoaderService,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.fuseConfigService.config = {
            layout: {
                navbar: { hidden: true },
                toolbar: { hidden: true },
                footer: { hidden: true },
                sidepanel: { hidden: true },
            },
        };
    }

    ngOnInit(): void {
        this.token = this.route.snapshot.queryParamMap.get("token");
        this.resetPasswordForm = this.formBuilder.group({
            password: ["", [Validators.required, Validators.minLength(this.minLength)]],
            confirmPassword: ["", [Validators.required]],
        });

        this.authService.getPasswordPolicy().subscribe({
            next: (policy) => {
                this.minLength = policy.minLength || this.minLength;
                this.resetPasswordForm.get("password").setValidators([Validators.required, Validators.minLength(this.minLength)]);
                this.resetPasswordForm.get("password").updateValueAndValidity();
            },
        });
    }

    submit(): void {
        if (!this.token) {
            this.error = "login.resetPasswordError.invalidToken";
            return;
        }

        if (this.resetPasswordForm.value.password !== this.resetPasswordForm.value.confirmPassword) {
            this.error = "login.resetPassword.validation.match";
            return;
        }

        this.loading = true;
        this.error = null;

        this.authService
            .resetPassword({
                token: this.token,
                password: this.resetPasswordForm.value.password,
            })
            .subscribe(
                () => {
                    this.authenticationService.logout();
                    this.success = true;
                    this.loading = false;
                },
                (error) => {
                    this.error = this.getErrorKey(error);
                    this.loading = false;
                },
            );
    }

    goToLogin(): void {
        this.router.navigate(["/login"]);
    }

    private getErrorKey(error: any): string {
        if (error?.error?.message) {
            return error.error.message;
        }

        if (error?._body) {
            try {
                const responseBody = JSON.parse(error._body);
                if (responseBody.message) {
                    return responseBody.message;
                }
            } catch (_parseError) {
                return "login.resetPasswordError.generic";
            }
        }

        return "login.resetPasswordError.generic";
    }
}
